import React from 'react';
import {
  Breakpoints,
  LoaderSpinnerModal,
  TextAppearance,
  TextColor,
  TextCustom,
  TextSize,
  TextTag,
  TextWeight,
} from '@vw-marketing/us-components';
import {StyledSimilarVehicles} from './styles';
import {SimilarVehiclesSlider} from '../similar-vehicles-slider';
import {useQuery} from '@apollo/client';
import {ErrorMessage} from '../error-message';
import {USED_INVENTORY_DATA} from '../../store/queries/used-vehicles-inventory';
import {
  DEFAULT_FILTERS_CONFIG,
  DEFAULT_RESULTS_SLIDER,
} from '../../utils/app-constants';
import {useTranslations} from '../../hooks/use-translations';
import {UsedVehicle} from '../../typing/used-inventory';
import {useFeatureAppConfig} from '../../hooks/use-feature-app-config';

interface SimilarVehiclesProps {
  readonly modelNames: string[];
  readonly zipcode: string;
  readonly priceDisclaimer?: string;
  readonly getWarrantyDisclaimer?: (year?: string) => string | undefined;
}

export const SimilarVehicles = (props: SimilarVehiclesProps): JSX.Element => {
  const {modelNames, zipcode, priceDisclaimer, getWarrantyDisclaimer} = props;
  const translations = useTranslations().similarVehicles;
  const {distance} = {
    ...DEFAULT_FILTERS_CONFIG,
    ...useFeatureAppConfig().filterConfig,
  };

  const queryVariables = {
    zipcode,
    distance: distance.plusValue,
    pageSize: DEFAULT_RESULTS_SLIDER,
    pageNumber: 0,
    sortBy: 'distance',
    filters: JSON.stringify({modelName: modelNames}),
  };

  const {data, loading, error} = useQuery(USED_INVENTORY_DATA, {
    variables: queryVariables,
  });

  const vehiclesData: UsedVehicle[] = data?.usedInventory?.vehicles;

  if (loading) return <LoaderSpinnerModal />;

  if (error)
    return <ErrorMessage message1={'Error loading vehicle details data'} />;

  return !!vehiclesData.length ? (
    <StyledSimilarVehicles className="similar-vehicles-container">
      <TextCustom
        appearance={TextAppearance.headline300}
        tag={TextTag.h2}
        style={{
          [Breakpoints.default]: {
            fontWeight: TextWeight.bold,
            color: TextColor.inherit,
            fontSize: TextSize.TextSize28,
          },
          [Breakpoints.b1280]: {
            fontSize: TextSize.TextSize32,
          },
          [Breakpoints.b2560]: {
            fontSize: TextSize.TextSize44,
          },
        }}
      >
        {translations.title}
      </TextCustom>
      <div className="divider" />
      <SimilarVehiclesSlider
        vehicles={vehiclesData}
        priceDisclaimer={priceDisclaimer}
        getWarrantyDisclaimer={getWarrantyDisclaimer}
      />
    </StyledSimilarVehicles>
  ) : (
    <></>
  );
};
